<template>
    <q-dialog v-model="showModal" @hide="onHide" @show="fetchProvinces">
        <q-card class="responsive-modal">
            <q-card-section class="items-center row q-pb-none">
                <q-icon name="location_on" size="md" color="primary" class="tw-mr-3" />
                <div class="text-h6">
                    {{ $t("province_selection.title") }}
                </div>
                <q-space />
            </q-card-section>

            <q-card-section class="tw-px-4 tw-py-6">
                <p class="tw-text-gray-600 tw-mb-4">
                    {{ $t("province_selection.description") }}
                </p>

                <div class="tw-mb-3 tw-p-3 tw-bg-blue-50 tw-border-l-4 tw-border-blue-400 tw-rounded">
                    <p class="tw-text-sm tw-text-blue-700">
                        <q-icon name="info" class="tw-mr-1" />
                        {{ $t("province_selection.none_option_info") }}
                    </p>
                </div>
                <div class="tw-mb-3 tw-p-3 tw-bg-red-50 tw-border-l-4 tw-border-red-400 tw-rounded"
                    v-if="selectedProvince != 'Todas' && selectedProvince != 'None' && selectedProvince">
                    <p class="tw-text-sm tw-text-red-700">
                        <q-icon name="info" class="tw-mr-1" />
                        {{ $t("province_selection.deleted_product_by_province") }}
                    </p>
                </div>

                <Select :label="$t('fields.province')" v-model="selectedComputed" :options="provinces"
                    :errors="provinceError" :loading="provincesLoading" clearable class="tw-w-full"
                    data-cy="province-select" />
            </q-card-section>

            <q-card-actions align="right" class="tw-px-4 tw-pb-4">
                <q-btn :label="$t('actions.cancel')" color="secondary" :disable="provincesLoading" @click="onHide" />
                <q-btn :label="$t('actions.save')" color="primary" :disabled="!selectedProvince || provincesLoading"
                    :loading="saving" @click="saveProvince" />
            </q-card-actions>
        </q-card>
    </q-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue"
import { useQuasar } from "quasar"
import { useI18n } from "vue-i18n"
import { ProvincesManager } from "~/common/ProvincesManager"

// Define emits
const emit = defineEmits<{
    provinceSaved: [province: string]
    hide: []
}>()

// Get instances of composables
const $q = useQuasar()
const i18n = useI18n()
const { selectedProvince } = useProvinceSelection()
// Props
const props = defineProps<{
    modelValue: boolean
}>()

// Internal modal state
const showModal = ref(props.modelValue)

// Form state
const provinceError = ref<string>("")
const saving = ref(false)

// Watch for prop changes
watch(() => props.modelValue, (newVal) => {
    showModal.value = newVal
})
const shopListStore = useShopListStore()

const url_computed = computed(() => {
    if (shopListStore.currentShop) {
        return `init-settings/provinces/store?store=${shopListStore.currentShop?.slug}`
    }
    return "init-settings/provinces/store"
}
)

// Fetch provinces data
const {
    get: fetchProvinces,
    data: provincesData,
    loading: provincesLoading,
} = useOFetchCustom(url_computed.value)



// Computed properties
const manager = computed<ProvincesManager | null>(() =>
    provincesData.value ? new ProvincesManager(provincesData.value.data) : null
)

const localSelected = ref<string>(selectedProvince.value || "")

watch(showModal, (isOpen) => {
    if (isOpen) {
        localSelected.value = selectedProvince.value || ""
    }
}, { immediate: true })

const selectedComputed = computed({
    get() {
        if (!localSelected.value)
            return i18n.t("province_selection.none_option")
        return localSelected.value
    },
    set(newValue: string) {
        localSelected.value = newValue
        selectedProvince.value = newValue
    }
})


const provinces = computed(() => {
    const provinceList = manager.value?.onlyProvinces() || []
    // Add "None" option at the beginning for other business logic
    return [i18n.t("province_selection.none_option") as string, ...provinceList]
})

// Methods
function validateProvince() {
    if (!selectedProvince.value?.trim()) {
        provinceError.value = i18n.t("messages.field_required") as string
        return false
    }

    provinceError.value = ""
    return true
}

function saveProvince() {
    if (!validateProvince()) {
        return
    }

    saving.value = true

    try {
        // Save to localStorage
        localStorage.setItem("selectedProvince", selectedProvince.value)

        // Emit success
        emit("provinceSaved", selectedProvince.value)

        // Show success notification
        $q.notify({
            type: "positive",
            message: i18n.t("province_selection.saved_successfully"),
            position: "top-right",
        })

        // Close modal
        showModal.value = false
        emit("hide")
    } catch (error) {
        console.error("Error saving province:", error)
        $q.notify({
            type: "negative",
            message: i18n.t("messages.error"),
            position: "top-right",
        })
    } finally {
        saving.value = false
    }
}

function onHide() {
    emit("hide")
}

</script>

<style scoped>
.responsive-modal {
    min-width: 60%;
    /* Default for phone screens */
}

@media (min-width: 768px) {
    .responsive-modal {
        min-width: 40%;
        /* For tablet and desktop screens */
    }
}
</style>
